// src/pages/ResetPassword.js
//
// Record 688 - the password reset page on our own domain (Greg Keeley's notes of 2026-10-01, 4-4.3; Bob's promise of
// 2026-10-02). The e-mail's link opens /auth/action?mode=resetPassword&oobCode=...; this page checks the code, asks for the
// new password twice, shows the requirements of the policy of record turning green as each is met, sets the password,
// and returns the member to sign-in (on its own after a few seconds, or by the button). Any other action mode (e-mail
// verification, recovery) is handed to the standard Firebase page unchanged.
import React, { useEffect, useMemo, useState } from 'react';
import { auth } from '../firebase';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { INSTALLATION } from '../installation';
import { parseAction, checklist, ready, resetError } from '../data/resetPassword';

const box = { maxWidth: 420, margin: '60px auto', background: '#13263A', border: '1px solid #2B5C8A', borderRadius: 10, padding: 28, color: '#DCE7F2', fontFamily: 'inherit' };
const input = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 6, border: '1px solid #2B5C8A', background: '#0B1626', color: '#F0F4F8', fontSize: 15, marginTop: 6 };
const btn = { width: '100%', padding: '11px 0', borderRadius: 6, border: 'none', background: '#E8A020', color: '#0B1626', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 16 };
const goSignIn = () => { window.location.href = '/'; };

export default function ResetPassword() {
  const action = useMemo(() => parseAction(window.location.search), []);
  const [email, setEmail] = useState('');
  const [state, setState] = useState('checking');   // checking | ready | saving | done | error
  const [err, setErr] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (action.mode !== 'resetPassword') {
      // Not ours: the standard Firebase handler on the project's own auth domain does every other action.
      window.location.replace('https://' + INSTALLATION.authDomain + '/__/auth/action' + window.location.search);
      return;
    }
    verifyPasswordResetCode(auth, action.oobCode)
      .then((e) => { setEmail(e); setState('ready'); })
      .catch((e) => { setErr(resetError(e && e.code)); setState('error'); });
  }, [action]);

  useEffect(() => {
    if (state !== 'done') return undefined;
    const t = setTimeout(goSignIn, 6000);
    return () => clearTimeout(t);
  }, [state]);

  const rows = checklist(pw, pw2);
  const save = async (e) => {
    e.preventDefault();
    if (!ready(pw, pw2)) return;
    setState('saving'); setErr('');
    try { await confirmPasswordReset(auth, action.oobCode, pw); setState('done'); }
    catch (e2) { setErr(resetError(e2 && e2.code)); setState(e2 && /weak-password|does-not-meet/.test(String(e2.code)) ? 'ready' : 'error'); }
  };

  return (
    <div style={box}>
      <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{INSTALLATION.platformName}</div>
      <div style={{ fontSize: 14, color: '#8AA4BF', marginBottom: 18 }}>Set a new password</div>
      {state === 'checking' && <div style={{ fontSize: 14 }}>Checking your reset link...</div>}
      {state === 'error' && (
        <>
          <div style={{ fontSize: 14, color: '#FF8A8A' }}>{err}</div>
          <button style={btn} onClick={goSignIn}>Go to sign-in</button>
        </>
      )}
      {state === 'done' && (
        <>
          <div style={{ fontSize: 14, color: '#5FB878' }}>Your password has been changed. Sign in with your new password.</div>
          <div style={{ fontSize: 12, color: '#8AA4BF', marginTop: 6 }}>Taking you to sign-in in a few seconds.</div>
          <button style={btn} onClick={goSignIn}>Go to sign-in now</button>
        </>
      )}
      {(state === 'ready' || state === 'saving') && (
        <form onSubmit={save}>
          <div style={{ fontSize: 13, color: '#8AA4BF' }}>For {email}</div>
          <label style={{ display: 'block', fontSize: 13, marginTop: 14 }}>New password
            <input aria-label="Your new password" style={input} type={show ? 'text' : 'password'} autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus />
          </label>
          <label style={{ display: 'block', fontSize: 13, marginTop: 12 }}>Type it again
            <input aria-label="New password" style={input} type={show ? 'text' : 'password'} autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
          </label>
          <label style={{ display: 'block', fontSize: 12, color: '#8AA4BF', marginTop: 8 }}>
            <input aria-label="Type it again" type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} /> Show the password
          </label>
          <ul style={{ listStyle: 'none', padding: 0, margin: '14px 0 0', fontSize: 13 }}>
            {rows.map((r) => (
              <li key={r.label} style={{ color: r.ok === true ? '#5FB878' : r.ok === false ? '#FF8A8A' : '#8AA4BF', margin: '3px 0' }}>
                {r.ok === true ? '✓' : r.ok === false ? '✗' : '•'} {r.label}
              </li>
            ))}
          </ul>
          {err && <div style={{ fontSize: 13, color: '#FF8A8A', marginTop: 10 }}>{err}</div>}
          <button style={{ ...btn, opacity: ready(pw, pw2) && state === 'ready' ? 1 : 0.5 }} disabled={!ready(pw, pw2) || state !== 'ready'} type="submit">
            {state === 'saving' ? 'Saving...' : 'Set my new password'}
          </button>
          <div style={{ fontSize: 12, color: '#8AA4BF', marginTop: 10 }}>Write it down before you save it.</div>
        </form>
      )}
    </div>
  );
}
